/*
  Widerrufsformular — custom UI on top of the Shopify Forms app block.

  The visible form (sections/ag-return-form.liquid) never posts by itself.
  This element copies the values into the form inside the shadow DOM of the
  Shopify Forms app block that sits in the same section, triggers that form's
  own submit and mirrors its success/error state back into the modal.

  This is an integration with the internal DOM of the Shopify Forms app, not a
  public API: after every change to the form in the app — and after app updates —
  re-check FIELD_MAP and the selectors in findBridge() against the shadow DOM.
*/
(() => {
  const ELEMENT_NAME = "ag-return-form-modal";
  const HTML_OPEN_CLASS = "ag-return-form-modal-is-open";
  const TRIGGER_SELECTOR = "[data-ag-return-form-trigger]";
  /* Convert matching menu links to buttons while this section is mounted. */
  const TRIGGER_SCOPE_SELECTOR = ".footer";
  /* Marks the app block so the CSS can move it off-screen. */
  const EMBED_TRANSPORT_CLASS = "ag-return-form-transport";

  /* Wait for the app block to mount, for its submit result, and for errors
     to settle before treating them as final. */
  const BRIDGE_TIMEOUT = 15000;
  const RESULT_TIMEOUT = 30000;
  const ERROR_SETTLE_DELAY = 900;
  /* Grace given to Shopify Forms after the captcha challenge closes, so it can
     still report its own success or error. */
  const CAPTCHA_SETTLE_DELAY = 8000;
  /* The challenge is driven by the visitor, so the result wait is open-ended
     while it is open — this is the hard stop that keeps the form from staying
     in the submitting state forever. */
  const RESULT_HARD_TIMEOUT = 240000;
  /* The challenge lives outside the embed, so it is polled, not observed. */
  const RESULT_POLL_INTERVAL = 400;

  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled]):not([type='hidden'])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  /*
    Shopify Forms gives the theme no callback for its captcha, so the open
    challenge is read off the iframe it appends to the document, outside the
    shadow root of the embed. The anchor and bootstrap frames of the same
    provider are tiny or hidden; only the challenge itself covers a real area
    and is visible up its whole ancestor chain.
  */
  const CAPTCHA_FRAME_SELECTOR = [
    'iframe[src*="hcaptcha.com"]',
    'iframe[src*="recaptcha"]',
    'iframe[title*="captcha" i]',
  ].join(",");
  const CAPTCHA_CHALLENGE_MIN_SIZE = 100;

  const isCaptchaChallengeOpen = () =>
    Array.from(document.querySelectorAll(CAPTCHA_FRAME_SELECTOR)).some(
      (frame) => {
        const rect = frame.getBoundingClientRect();
        if (
          rect.width < CAPTCHA_CHALLENGE_MIN_SIZE ||
          rect.height < CAPTCHA_CHALLENGE_MIN_SIZE
        ) {
          return false;
        }

        for (
          let node = frame;
          node instanceof HTMLElement;
          node = node.parentElement
        ) {
          const style = window.getComputedStyle(node);
          if (
            style.display === "none" ||
            style.visibility === "hidden" ||
            style.opacity === "0"
          ) {
            return false;
          }
        }

        return true;
      },
    );

  const DEFAULT_STRINGS = {
    required: "Bitte überprüfen Sie Ihre Eingaben und füllen Sie alle Pflichtfelder aus",
    email: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
    network: "Die Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es später erneut.",
  };

  /* Fields that must be filled before the submit is handed over. */
  const REQUIRED_FIELDS = ["first_name", "order_number", "email"];

  /*
    Fields marked with this attribute accept digits only (Bestellnummer). Typed
    and pasted text is stripped in place, so the value can never be malformed
    and the field only ever fails the required check.
  */
  const DIGITS_ONLY_ATTRIBUTE = "data-ag-return-form-digits";

  const stripNonDigits = (field) => {
    const { value } = field;
    const digits = value.replace(/\D+/g, "");
    if (digits === value) return;

    /* Keep the caret where it was instead of letting it jump to the end. */
    const caret = field.selectionStart ?? value.length;
    const removed = value.slice(0, caret).replace(/\d+/g, "").length;

    field.value = digits;
    field.setSelectionRange?.(caret - removed, caret - removed);
  };

  /*
    Map of the visible fields onto the fields of the Shopify Forms form
    (form id 1170558, "Widerrufsformular"). `ids` are matched against id, name
    and data-testid, from the most precise handle to the least; `match` is the
    semantic fallback used when a custom field handle changes. Read the real
    handles in DevTools: <shopify-forms-embed> → #shadow-root →
    form[data-testid="form"].
  */
  const FIELD_MAP = [
    {
      name: "first_name",
      selector: "input:not([type='file'])",
      ids: ["first_name"],
      match: /vor.?und.?nachname|first.?name/,
    },
    {
      /* The trailing underscore is part of the generated handle. */
      name: "order_number",
      selector: "input:not([type='file'])",
      ids: ["custom#bestellnummer_", "custom#bestellnummer"],
      match: /bestellnummer|order.?number/,
    },
    {
      name: "email",
      selector: "input:not([type='file'])",
      ids: ["email"],
      match: /e-?mail/,
    },
    {
      /* The textarea carries only a name, no id; the trailing number is part
         of the handle the app generated when the field was recreated. */
      name: "body",
      selector: "textarea",
      ids: ["custom#kommentar_1", "custom#kommentar", "custom#nachricht"],
      match: /kommentar|nachricht|comment|message/,
    },
    {
      /*
        Marks the submission as the Widerrufsformular — what the hidden
        `contact[type]` field did on the native contact form. Add a short text
        field named "Formulartyp" to the form in the Shopify Forms app and it is
        filled from here; because the app block sits off-screen, the field never
        appears in the UI. Optional: without it the submit still goes through,
        and the form itself already identifies the submission in the app.
      */
      name: "form_type",
      selector: "input:not([type='file'])",
      ids: ["custom#formulartyp", "custom#formular_typ", "custom#form_type"],
      match: /formulartyp|formular.?typ|form.?type/,
      value: "Widerrufsformular",
      optional: true,
    },
  ];

  /* Semantic fallback: id/name/testid/label/container text of the field. */
  const findFieldBySemantics = (root, selector, match) => {
    if (!root) return null;

    return (
      Array.from(root.querySelectorAll(selector)).find((field) => {
        const labels = field.labels
          ? Array.from(field.labels)
              .map((label) => label.textContent || "")
              .join(" ")
          : "";
        const container =
          field.closest('[class*="formFieldContainer"], [class*="formField"]')
            ?.textContent || "";

        return match.test(
          [
            field.id,
            field.name,
            field.dataset.testid,
            field.getAttribute("aria-label"),
            labels,
            container,
          ]
            .join(" ")
            .toLowerCase(),
        );
      }) || null
    );
  };

  const findField = (root, entry) => {
    if (!root) return null;

    for (const handle of entry.ids) {
      const field =
        root.getElementById(handle) ||
        root.querySelector(`[name="${handle}"]`) ||
        root.querySelector(`[data-testid="field-${handle}"]`);
      if (field) return field;
    }

    return findFieldBySemantics(root, entry.selector, entry.match);
  };

  /*
    Shopify Forms uses controlled inputs: a plain `.value` assignment can update
    the DOM without updating the state that ends up in the payload.
  */
  const setNativeValue = (input, value) => {
    if (!input) return;

    const prototype =
      input instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, "value");

    if (descriptor?.set) {
      descriptor.set.call(input, value);
    } else {
      input.value = value;
    }

    input.dispatchEvent(new Event("input", { bubbles: true, composed: true }));
    input.dispatchEvent(new Event("change", { bubbles: true, composed: true }));
  };

  class AgReturnFormModal extends HTMLElement {
    constructor() {
      super();

      this.isSubmitting = false;
      this.lastTrigger = null;
      this.triggers = new Map();
      this.bridgeFields = null;
      /* Identifies the running submit attempt: closing or resetting the popup
         bumps it, which makes every result of an older attempt a no-op. */
      this.submissionId = 0;
      /* The embed cannot be re-mounted, so a successful submission is final
         for this page view and the popup keeps showing the success panel. */
      this.hasSucceeded = false;
      /* Fields the visitor has actually typed in: only those are validated on
         blur, so tabbing through the form does not turn it red. */
      this.touchedFields = new Set();

      this.handleOverlayClick = this.handleOverlayClick.bind(this);
      this.handleCloseClick = this.handleCloseClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleFieldInput = this.handleFieldInput.bind(this);
      this.handleFieldBlur = this.handleFieldBlur.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = this.querySelector(".ag-return-form");
      this.overlay = this.querySelector("[data-ag-return-form-overlay]");
      this.dialog = this.querySelector('[role="dialog"]');
      this.messages = this.querySelector("[data-ag-return-form-messages]");
      this.messageTemplate = this.querySelector(
        "[data-ag-return-form-message-template]",
      );
      this.successPanel = this.querySelector("[data-ag-return-form-success]");
      this.submitButton = this.querySelector("[data-ag-return-form-submit]");
      this.fields = Array.from(
        this.querySelectorAll("[data-ag-return-form-field]"),
      );
      this.strings = this.readStrings();

      this.overlay?.addEventListener("click", this.handleOverlayClick);
      this.form?.addEventListener("submit", this.handleSubmit, true);
      this.addEventListener("click", this.handleCloseClick);
      this.addEventListener("keydown", this.handleKeydown);

      this.fields.forEach((field) => {
        field.addEventListener("input", this.handleFieldInput);
        field.addEventListener("blur", this.handleFieldBlur);
      });

      this.hideEmbed();
      if (this.findEmbed()) this.watchEmbedTabOrder();
      this.setAccessibility(this.isOpen);
      this.bindTriggers();
    }

    disconnectedCallback() {
      this.initialized = false;
      this.submissionId += 1;
      this.transportController?.abort();
      window.clearTimeout(this.tabOrderTimer);
      this.overlay?.removeEventListener("click", this.handleOverlayClick);
      this.form?.removeEventListener("submit", this.handleSubmit, true);
      this.removeEventListener("click", this.handleCloseClick);
      this.removeEventListener("keydown", this.handleKeydown);
      this.fields.forEach((field) => {
        field.removeEventListener("input", this.handleFieldInput);
        field.removeEventListener("blur", this.handleFieldBlur);
      });
      document.documentElement.classList.remove(HTML_OPEN_CLASS);
      this.classList.remove("is-open");
      this.setAccessibility(false);
      const returnFocus = this.triggers.get(this.lastTrigger) || this.lastTrigger;
      this.triggers.forEach((link, button) => button.replaceWith(link));
      this.triggers.clear();
      if (this.lastTrigger) returnFocus?.focus({ preventScroll: true });
      this.lastTrigger = null;
    }

    readStrings() {
      const node = this.querySelector("[data-ag-return-form-strings]");
      if (!node) return { ...DEFAULT_STRINGS };

      try {
        return { ...DEFAULT_STRINGS, ...JSON.parse(node.textContent) };
      } catch (error) {
        console.error("ag-return-form: unable to parse strings", error);
        return { ...DEFAULT_STRINGS };
      }
    }

    /*
      Match the original menu handle so translated labels still open the popup.
      Keep title matching for older footer markup without handle attributes.
    */
    bindTriggers(root = document) {
      const title = (this.dataset.agReturnFormTriggerTitle || "").trim();
      if (title === "") return;
      const handle = (this.dataset.agReturnFormTriggerHandle || "").trim();

      this.triggers.forEach((_link, button) => {
        if (!button.isConnected) this.triggers.delete(button);
      });
      root.querySelectorAll(`${TRIGGER_SCOPE_SELECTOR} a`).forEach((link) => {
        const matchesHandle = handle !== "" && link.dataset.agFooterLinkHandle === handle;
        if (!matchesHandle && link.textContent.trim().toLowerCase() !== title) return;
        const button = document.createElement("button");
        button.type = "button";
        button.className = link.className;
        button.classList.add("footer__ag-action");
        button.textContent = link.textContent;
        button.setAttribute("data-ag-return-form-trigger", "");
        button.setAttribute("aria-haspopup", "dialog");
        if (this.id) button.setAttribute("aria-controls", this.id);
        button.setAttribute("aria-expanded", String(this.isOpen));
        this.triggers.set(button, link);
        link.replaceWith(button);
      });
    }

    setAccessibility(isOpen) {
      this.toggleAttribute("inert", !isOpen);
      this.setAttribute("aria-hidden", String(!isOpen));
      if (isOpen) this.dialog?.setAttribute("aria-modal", "true");
      else this.dialog?.removeAttribute("aria-modal");
      this.triggers.forEach((_link, button) => button.setAttribute("aria-expanded", String(isOpen)));
    }

    get isOpen() {
      return this.classList.contains("is-open");
    }

    open(trigger) {
      if (this.isOpen) return;

      this.lastTrigger = trigger || null;
      this.classList.add("is-open");
      this.setAccessibility(true);
      document.documentElement.classList.add(HTML_OPEN_CLASS);

      const focusTarget = this.classList.contains("is-success")
        ? this.successPanel?.querySelector(FOCUSABLE_SELECTOR) || this.successPanel
        : this.fields[0] || this.dialog;
      // Let the scrollable dialog reveal the field or button at browser zoom.
      focusTarget?.focus();
      this.ensureTransport().catch((error) => {
        if (error.name !== "AbortError") {
          console.error("ag-return-form: unable to load Shopify Forms", error);
        }
      });
    }

    /* Fetch the configured app block only when the popup is needed. The
       alternate template keeps Forms out of the initial content_for_header;
       only its official loader is executed, never the fetched page scripts. */
    ensureTransport() {
      if (this.findEmbed()) return Promise.resolve();
      if (!this.dataset.agReturnFormTransportUrl) return Promise.resolve();
      if (this.transportPromise) return this.transportPromise;

      const saved = AgReturnFormModal.transport;
      if (saved?.querySelector("shopify-forms-embed")) {
        const target = this.getScope()?.querySelector("[data-ag-return-form-transport]");
        if (target && target !== saved) {
          target.replaceChildren(...saved.childNodes);
          AgReturnFormModal.transport = target;
          this.hideEmbed();
          this.watchEmbedTabOrder();
          return Promise.resolve();
        }
      }

      this.transportController = new AbortController();
      const { signal } = this.transportController;
      this.transportPromise = this.loadTransport(signal).catch((error) => {
        this.transportPromise = null;
        throw error;
      });
      return this.transportPromise;
    }

    async loadTransport(signal) {
      const target = this.getScope()?.querySelector("[data-ag-return-form-transport]");
      if (!target) throw new Error("Missing return form transport container");
      const response = await fetch(this.dataset.agReturnFormTransportUrl, { signal });
      if (!response.ok) throw new Error(`Return form template: ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), "text/html");
      signal.throwIfAborted();
      const content = html.querySelector("[data-ag-return-form-transport-content]");
      const loader = Array.from(html.scripts).find((script) => {
        const url = new URL(script.src || "/", location.href);
        return url.hostname === "cdn.shopify.com" &&
          url.pathname.endsWith("/assets/shopify-forms-loader.js");
      });
      if (!content?.querySelector("[data-form-root]") || !loader) {
        throw new Error("Shopify Forms block is missing from the transport template");
      }
      content.querySelectorAll("script").forEach((script) => script.remove());
      target.replaceChildren(...content.childNodes);
      window.ShopifyForms = {
        ...window.ShopifyForms,
        currentPageType: this.dataset.agReturnFormPageType,
      };
      const existing = Array.from(document.scripts).find((script) => script.src === loader.src);
      if (!existing) {
        await new Promise((resolve, reject) => {
          const script = document.createElement("script");
          script.src = loader.src;
          script.async = true;
          script.onload = resolve;
          script.onerror = () => {
            script.remove();
            target.replaceChildren();
            reject(new Error("Shopify Forms loader failed"));
          };
          document.head.append(script);
        });
      }
      if (!signal.aborted) {
        AgReturnFormModal.transport = target;
        this.hideEmbed();
        this.watchEmbedTabOrder();
      }
    }

    close() {
      if (!this.isOpen) return;

      /* Closing while the visitor is solving the challenge would abandon a
         submit that is already on its way, so the popup stays put until the
         challenge is gone — overlay, close button and Escape alike. */
      if (this.isSubmitting && isCaptchaChallengeOpen()) return;

      this.classList.remove("is-open");
      document.documentElement.classList.remove(HTML_OPEN_CLASS);

      this.lastTrigger?.focus({ preventScroll: true });
      if (this.contains(document.activeElement)) document.activeElement.blur();
      this.setAccessibility(false);
      this.lastTrigger = null;

      /* Closing always wipes the form — entered values, error state and the
         general message alike — so it opens fresh next time, not only after
         a successful submit. */
      this.reset();
    }

    reset() {
      /* Invalidates a submit that is still running: its result must not land
         in the popup that is being reset, and the button is released here
         instead of in the finally block of that stale attempt. */
      this.submissionId += 1;
      this.setSubmitting(false);

      /*
        A successful submission survives the reset. Shopify Forms leaves its
        embed in the success step and cannot be re-mounted, so an empty form
        here would look usable and silently fail on the next submit — the
        success panel stays until the page is reloaded.
      */
      if (!this.hasSucceeded) {
        this.classList.remove("is-success");
        this.successPanel?.setAttribute("hidden", "");
        this.form?.reset();
      }

      this.clearMessages();

      /* form.reset() restores the values but not the error state. */
      this.touchedFields.clear();
      this.fields.forEach((field) => this.setFieldValidity(field, true));
    }

    handleOverlayClick() {
      this.close();
    }

    handleCloseClick(event) {
      if (event.target.closest("[data-ag-return-form-close]")) {
        event.preventDefault();
        this.close();
      }
    }

    handleKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === "Tab") this.trapFocus(event);
    }

    trapFocus(event) {
      const focusable = Array.from(
        this.dialog?.querySelectorAll(FOCUSABLE_SELECTOR) || [],
      ).filter((node) => node.tabIndex >= 0 && node.offsetParent !== null &&
        !node.closest('[hidden], [inert]') && window.getComputedStyle(node).visibility !== "hidden");

      if (focusable.length === 0) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && (document.activeElement === first || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !focusable.includes(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    }

    handleFieldInput(event) {
      const field = event.target;

      if (field.hasAttribute(DIGITS_ONLY_ATTRIBUTE)) stripNonDigits(field);

      this.touchedFields.add(field);
      this.clearMessages();
      this.setFieldValidity(field, true);
    }

    /*
      The error state of a field is shown as soon as the visitor leaves it, not
      only once the submit button is used. Fields that were never typed in stay
      neutral — an empty form should not look broken before it is filled in.
    */
    handleFieldBlur(event) {
      const field = event.target;
      // A server error remains valid until the visitor edits this field.
      if (!this.touchedFields.has(field) || field.getAttribute("aria-invalid") === "true") return;

      this.setFieldValidity(field, this.fieldError(field) === null);
    }

    setFieldValidity(field, valid, message) {
      field.classList.toggle("is-invalid", !valid);
      const error = this.querySelector(
        `[data-ag-return-form-error="${field.dataset.agReturnFormField}"]`,
      );
      const descriptions = new Set((field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean));

      if (error) {
        error.hidden = valid;
        error.textContent = valid ? "" : message || this.strings[this.fieldError(field) || "required"];
        if (valid) descriptions.delete(error.id);
        else descriptions.add(error.id);
        if (descriptions.size) field.setAttribute("aria-describedby", [...descriptions].join(" "));
        else field.removeAttribute("aria-describedby");
      }

      if (valid) {
        field.removeAttribute("aria-invalid");
      } else {
        field.setAttribute("aria-invalid", "true");
      }
    }

    /* Returns the key of the failed rule, or null when the field is fine. */
    fieldError(field) {
      const name = field.dataset.agReturnFormField;
      const value = field.value.trim();

      if (REQUIRED_FIELDS.includes(name) && value === "") return "required";
      if (name === "email" && value !== "" && !EMAIL_PATTERN.test(value)) {
        return "email";
      }

      return null;
    }

    fieldByName(name) {
      return this.fields.find(
        (field) => field.dataset.agReturnFormField === name,
      );
    }

    valueOf(name) {
      return this.fieldByName(name)?.value.trim() || "";
    }

    validate() {
      let valid = true;
      let emailMalformed = false;

      this.fields.forEach((field) => {
        /* After a submit every field is under validation on blur as well. */
        this.touchedFields.add(field);

        const error = this.fieldError(field);
        this.setFieldValidity(field, error === null);

        if (error === null) return;
        valid = false;
        if (error === "email") emailMalformed = true;
      });

      return { valid, emailMalformed };
    }

    focusInvalidField() {
      this.fields.find((field) => field.getAttribute("aria-invalid") === "true")?.focus();
    }

    clearMessages() {
      if (!this.messages) return;
      this.messages.innerHTML = "";
      this.messages.setAttribute("hidden", "");
    }

    showMessages(texts) {
      if (!this.messages || texts.length === 0) return;

      this.messages.innerHTML = "";

      texts.forEach((text) => {
        const fragment = this.messageTemplate?.content.cloneNode(true);
        if (!fragment) return;
        const target = fragment.querySelector(
          "[data-ag-return-form-message-text]",
        );
        if (target) target.textContent = text;
        this.messages.appendChild(fragment);
      });

      this.messages.removeAttribute("hidden");
    }

    showSuccess() {
      this.clearMessages();
      this.hasSucceeded = true;
      this.classList.add("is-success");
      this.successPanel?.removeAttribute("hidden");
      this.successPanel
        ?.querySelector(FOCUSABLE_SELECTOR)
        ?.focus();
      if (!this.successPanel?.contains(document.activeElement)) this.successPanel?.focus();
    }

    setSubmitting(state) {
      this.isSubmitting = state;
      if (!this.submitButton) return;
      // Keep the active button in the tab order while the request is pending.
      this.submitButton.setAttribute("aria-disabled", String(state));
      this.submitButton.setAttribute("aria-busy", String(state));
    }

    async handleSubmit(event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (this.isSubmitting || !this.form) return;

      this.clearMessages();

      const { valid, emailMalformed } = this.validate();
      if (!valid) {
        this.showMessages([
          emailMalformed ? this.strings.email : this.strings.required,
        ]);
        this.focusInvalidField();
        return;
      }

      /*
        The submit outlives the popup: closing or resetting it bumps the id, so
        a result that arrives late can no longer paint success or an error over
        a popup that has been closed or opened again in the meantime.
      */
      const attempt = ++this.submissionId;
      const isCurrent = () => attempt === this.submissionId;

      this.setSubmitting(true);
      let restoreScroll = null;

      try {
        await this.ensureTransport();
        const bridge = await this.waitForBridge();
        if (!isCurrent()) return;

        /*
          Every native field is rewritten right before the submit, empty ones
          included, so an old autofill value cannot survive in the hidden form.
        */
        FIELD_MAP.forEach((entry) => {
          const field = bridge.fields[entry.name];
          if (!field) return;
          setNativeValue(field, entry.value ?? this.valueOf(entry.name));
        });

        restoreScroll = this.preventEmbedAutoScroll();
        this.takeEmbedOutOfTabOrder();
        this.submitBridge(bridge);
        await this.waitForResult(bridge);
        if (!isCurrent()) return;

        this.showSuccess();
        this.form.reset();
      } catch (error) {
        if (!isCurrent()) return;

        /* A challenge the visitor closed without solving is not a failure:
           the entered values stay in the form and the submit can simply be
           repeated, so no message is shown for it. */
        if (error?.captchaAbandoned) return;

        console.error("ag-return-form: submit failed", error);
        if (!this.applyFieldErrors(error)) {
          this.showMessages([this.strings.network]);
        }
      } finally {
        restoreScroll?.();
        if (isCurrent()) this.setSubmitting(false);
      }
    }

    /*
      The app block must be looked up inside this section only — a page-wide
      search could hand the values to a different Shopify Forms embed.
    */
    getScope() {
      const section = this.closest(".shopify-section");
      if (section) return section;

      /* Standalone rendering: safe only while the page holds a single embed. */
      return document.querySelectorAll("shopify-forms-embed").length === 1
        ? document
        : null;
    }

    findEmbed() {
      const scope = this.getScope();
      const embed =
        scope?.querySelector("shopify-forms-embed#app-embed") ||
        scope?.querySelector("shopify-forms-embed");

      return embed ? { scope, embed } : null;
    }

    /*
      The app block stays mounted, keeps a layout box and stays inside the
      viewport: `display: none`, `hidden` and an off-screen position all make
      the embed look invisible or out of view to the captcha, which answers
      with more challenges. It is shrunk to a transparent 1×1 box instead; the
      class is a fallback for browsers without `:has()` support, see
      component-ag-return-form.css.
    */
    hideEmbed() {
      const embed = this.findEmbed()?.embed;
      if (!embed) return;

      const block = embed.closest(".shopify-app-block, .shopify-block") || embed;
      block.classList.add(EMBED_TRANSPORT_CLASS);
      block.setAttribute("aria-hidden", "true");
    }

    /*
      Being inside the viewport, the fields and the submit button of the app
      would otherwise be reachable with Tab from the visible popup, so they are
      taken out of the tab order. `inert` is deliberately not used: it would
      also block the programmatic requestSubmit() this integration runs on.
    */
    takeEmbedOutOfTabOrder() {
      const root = this.findEmbed()?.embed.shadowRoot;
      const focusable = root?.querySelectorAll(FOCUSABLE_SELECTOR);
      if (!focusable?.length) return false;

      /* Only actual changes are written, so this cannot loop with the app's
         own re-renders. */
      focusable.forEach((node) => {
        if (node.tabIndex !== -1) node.tabIndex = -1;
      });

      return true;
    }

    /* The app mounts asynchronously and re-renders its fields, so the tab
       order is re-applied until it sticks and again around every submit. */
    watchEmbedTabOrder(attempts = 40) {
      if (this.takeEmbedOutOfTabOrder() || attempts <= 0) return;
      window.clearTimeout(this.tabOrderTimer);
      this.tabOrderTimer = window.setTimeout(() => this.watchEmbedTabOrder(attempts - 1), 250);
    }

    findBridge() {
      const found = this.findEmbed();
      if (!found) return null;

      const { scope, embed } = found;
      const root = embed.shadowRoot;
      const appRoot =
        root?.querySelector("#app-embed[data-current-step]") ||
        root?.querySelector("[data-current-step]");
      const form =
        root?.querySelector('form[data-testid="form"]') ||
        root?.querySelector("form");
      const submitButton =
        root?.querySelector('[data-testid="btn-form-submit"]') ||
        form?.querySelector('button[type="submit"], input[type="submit"]');

      /* appRoot is the success indicator, not a precondition: waitForResult
         falls back to the markup when the shadow root has no step attribute. */
      if (!root || !form || !submitButton) return null;

      /* Fail closed: a missing field must block the submit, never send a
         partially filled form. Only fields marked optional may be absent. */
      const fields = {};
      for (const entry of FIELD_MAP) {
        const field = findField(root, entry);
        if (!field && !entry.optional) return null;
        if (field) fields[entry.name] = field;
      }

      return { scope, embed, root, appRoot, form, submitButton, fields };
    }

    waitForBridge(timeout = BRIDGE_TIMEOUT) {
      const startedAt = Date.now();

      return new Promise((resolve, reject) => {
        const tick = () => {
          const bridge = this.findBridge();

          if (bridge) {
            this.bridgeFields = bridge.fields;
            this.hideEmbed();
            this.takeEmbedOutOfTabOrder();
            resolve(bridge);
            return;
          }

          if (Date.now() - startedAt >= timeout) {
            reject(
              new Error(
                "ag-return-form: the Shopify Forms app block was not found or did not mount in time.",
              ),
            );
            return;
          }

          window.setTimeout(tick, 150);
        };

        tick();
      });
    }

    submitBridge(bridge) {
      if (bridge.form.requestSubmit) {
        bridge.form.requestSubmit(bridge.submitButton);
        return;
      }

      bridge.submitButton.click();
    }

    /* Shopify Forms scrolls to its own form on submit, which would jump the
       page to the off-screen app block behind the modal. */
    preventEmbedAutoScroll() {
      const originalScrollTo = window.scrollTo;
      const guardedScrollTo = (...args) => {
        if (this.isSubmitting && args[0]?.behavior === "smooth") return;
        return originalScrollTo.apply(window, args);
      };

      window.scrollTo = guardedScrollTo;

      return () => {
        if (window.scrollTo === guardedScrollTo) window.scrollTo = originalScrollTo;
      };
    }

    waitForResult(bridge, timeout = RESULT_TIMEOUT) {
      const startedAt = Date.now();
      const hardDeadline = startedAt + RESULT_HARD_TIMEOUT;

      return new Promise((resolve, reject) => {
        let observer = null;
        let pollId = null;
        let errorCheckId = null;
        let completed = false;
        let firstErrorSeenAt = 0;
        let deadline = startedAt + timeout;
        let captchaWasOpen = false;

        const cleanup = () => {
          completed = true;
          observer?.disconnect();
          if (pollId) window.clearInterval(pollId);
          if (errorCheckId) window.clearTimeout(errorCheckId);
        };

        const finishResolve = () => {
          if (completed) return true;
          cleanup();
          resolve();
          return true;
        };

        const finishReject = (error) => {
          if (completed) return true;
          cleanup();
          reject(error);
          return true;
        };

        const scheduleErrorRecheck = () => {
          if (errorCheckId) return;

          const elapsed = Date.now() - firstErrorSeenAt;
          errorCheckId = window.setTimeout(() => {
            errorCheckId = null;
            checkState();
          }, Math.max(50, ERROR_SETTLE_DELAY - elapsed + 25));
        };

        const checkState = () => {
          if (completed) return true;

          const appRoot =
            bridge.root.querySelector("#app-embed[data-current-step]") ||
            bridge.root.querySelector("[data-current-step]") ||
            bridge.appRoot;

          /* Without a step attribute, the app replacing its form with the
             success message is the signal. */
          const succeeded =
            appRoot?.getAttribute("data-current-step") === "success" ||
            (!appRoot &&
              (!bridge.root.contains(bridge.form) ||
                Boolean(bridge.root.querySelector('[data-testid*="success"]'))));

          if (succeeded) return finishResolve();

          /*
            A visible captcha challenge means the visitor, not the network, is
            the slow part here: the result timeout is suspended while it is
            open and restarted with a short grace once it closes, so the
            regular 30 seconds can no longer cut the wait short and turn a
            solved challenge into a network error.
          */
          if (isCaptchaChallengeOpen()) {
            captchaWasOpen = true;
            firstErrorSeenAt = 0;
            deadline = Date.now() + CAPTCHA_SETTLE_DELAY;
            return false;
          }

          const fieldErrors = this.collectFieldErrors(bridge);

          /*
            The app repaints its validation state during the submit, so an
            error only counts once it survived ERROR_SETTLE_DELAY.
          */
          if (Object.keys(fieldErrors).length) {
            firstErrorSeenAt = firstErrorSeenAt || Date.now();

            if (Date.now() - firstErrorSeenAt >= ERROR_SETTLE_DELAY) {
              const error = new Error(
                "ag-return-form: Shopify Forms reported validation errors.",
              );
              error.fieldErrors = fieldErrors;
              return finishReject(error);
            }

            scheduleErrorRecheck();
            return false;
          }

          firstErrorSeenAt = 0;

          if (Date.now() >= hardDeadline) {
            return finishReject(
              new Error(
                "ag-return-form: Shopify Forms did not report a result within the overall limit.",
              ),
            );
          }

          if (Date.now() >= deadline) {
            /* The challenge was closed without a result: the visitor most
               likely dismissed it, which the caller handles by releasing the
               form instead of showing the network error. */
            const error = new Error(
              captchaWasOpen
                ? "ag-return-form: the captcha challenge was closed without a result."
                : "ag-return-form: Shopify Forms did not report a result in time.",
            );
            if (captchaWasOpen) error.captchaAbandoned = true;

            return finishReject(error);
          }

          return false;
        };

        if (checkState()) return;

        observer = new MutationObserver(() => checkState());
        observer.observe(bridge.root, {
          attributes: true,
          childList: true,
          subtree: true,
        });

        /* The challenge iframe is outside the observed shadow root, and its
           opening and closing are exactly what the deadline depends on, so the
           state is polled as well. */
        pollId = window.setInterval(() => checkState(), RESULT_POLL_INTERVAL);
      });
    }

    collectFieldErrors(bridge) {
      const invalidFields = Array.from(
        bridge.root.querySelectorAll(
          'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"]',
        ),
      );

      const fieldErrors = invalidFields.reduce((errors, field) => {
        const key =
          field.name || field.id || field.dataset.testid?.replace(/^field-/, "");
        if (key) errors[key] = this.readFieldErrorMessage(field) || "";
        return errors;
      }, {});

      if (!Object.keys(fieldErrors).length) {
        const generalErrors = Array.from(
          bridge.root.querySelectorAll('[role="alert"], [data-testid*="error"]'),
        )
          .map((element) => element.textContent?.trim())
          .filter(Boolean);

        if (generalErrors.length) fieldErrors.general = generalErrors.join(" ");
      }

      return fieldErrors;
    }

    readFieldErrorMessage(field) {
      const describedBy = field.getAttribute("aria-describedby");

      if (describedBy) {
        const text = field
          .getRootNode()
          ?.getElementById?.(describedBy)
          ?.textContent?.trim();
        if (text) return text;
      }

      const container = field.closest(
        '[class*="formFieldContainer"], [class*="formField"]',
      );
      const errorElement = container?.querySelector(
        '[role="alert"], [data-testid*="error"], [class*="error"], [class*="Error"]',
      );

      return errorElement?.textContent?.trim() || "";
    }

    /* Errors are matched by the name/id of the native field that was actually
       found, so a renamed custom field handle cannot silently mismatch. */
    applyFieldErrors(error) {
      const fieldErrors = error?.fieldErrors;
      if (!fieldErrors) return false;

      const invalid = [];
      const texts = [];

      Object.entries(fieldErrors).forEach(([key, message]) => {
        const entry = FIELD_MAP.find((item) => {
          const native = this.bridgeFields?.[item.name];
          return native && (native.name === key || native.id === key);
        });

        if (entry) invalid.push({ name: entry.name, message });
        if (message) texts.push(message);
      });

      if (invalid.length === 0 && texts.length === 0) return false;

      invalid.forEach(({ name, message }) => {
        const field = this.fieldByName(name);
        if (field) this.setFieldValidity(field, false, message);
      });
      this.showMessages(texts.length > 0 ? texts : [this.strings.required]);
      this.focusInvalidField();
      return true;
    }

  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, AgReturnFormModal);
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest(TRIGGER_SELECTOR);
    if (!trigger) return;

    const modal = document.getElementById(trigger.getAttribute("aria-controls")) ||
      document.querySelector("[data-ag-return-form-modal]");
    if (!modal) return;

    event.preventDefault();
    modal.open(trigger);
  });

  /* Re-rendering the footer in the theme editor drops the trigger attributes,
     so they are applied again after every section load. */
  document.addEventListener("shopify:section:load", (event) => {
    document
      .querySelectorAll("[data-ag-return-form-modal]")
      .forEach((modal) => modal.bindTriggers?.(event.target));
  });
})();
